import * as API_V1 from 'patientOnboarding/api/v2/abie';
import * as Effects from 'redux-saga/effects';
import { all, put, select, takeLatest } from 'redux-saga/effects';

import {
  activeFiltersArraySelector,
  cliniciansSelector,
  filteredCliniciansSelector,
} from 'patientOnboarding/store/cliniciansGlobal/selectors';

import {
  getQueryString,
  LICENSED_CLINICIAN as licenseCredential,
  SUPERVISED_CLINICIAN as supervisedCredential,
} from 'patientOnboarding/utils';
import { filterClinicians } from 'patientOnboarding/utils/filterClinicians';
import { sortClinicians } from 'patientOnboarding/utils/sortClinicians';

import {
  loadClinicians,
  setClinicianCount,
  setDisplayedClinicians,
  setError,
  setFilteredClinicians,
  setLoadingClinicians,
  setLoadingCount,
  setLoadingDisplayedClinicians,
  setPage,
  setTotalPages,
} from './slice';
import t from './types';

const { call, delay } = Effects;

function* handleGetCliniciansCount(action: any): any {
  try {
    yield put(setLoadingCount(true));
    const preferences: any = {};
    for (const [key, value] of Object.entries(action.payload.activeFilters)) {
      if (Array.isArray(value) && value.length === 1 && key !== 'license_keys') {
        preferences[key] = value.toString();
      } else if (key === 'special_cases' && value === 'None of the above') {
        // Do nothing
      } else {
        preferences[key] = value;
      }
    }

    const queryString = getQueryString(preferences);
    const response = yield call(API_V1.getAllCliniciansAbie, { preferences: queryString });

    yield put(
      loadClinicians(
        response.data.clinicians.map((o: any) => {
          const isSupervised = o?.addresses[0]?.supervised_insurances.find(
            (f: any) => preferences.insurances === f.name,
          );
          if (isSupervised) {
            return {
              ...o,
              license_types: [...o.license_types, { ...supervisedCredential }],
            };
          }
          return {
            ...o,
            license_types: [...o.license_types, { ...licenseCredential }],
          };
        }),
      ),
    );
    yield put(setLoadingCount(false));
  } catch (e: any) {
    yield put(setError(e.response?.status));
    yield put(setLoadingCount(false));
  }
}

function* handleGetFilteredClinicians(): any {
  const clinicians = yield select(cliniciansSelector);
  const activeFilters = yield select(activeFiltersArraySelector);
  const { clinicians: filteredClinicians, count } = filterClinicians(clinicians, activeFilters);
  const sortedClinicians = sortClinicians(filteredClinicians, activeFilters);
  const totalPages = Math.ceil(count / 10);
  yield put(setFilteredClinicians(sortedClinicians));
  yield put(setTotalPages(totalPages));
}

function* handleGetClinicians(action: any): any {
  try {
    yield put(setLoadingClinicians(true));
    const preferences: any = {};
    for (const [key, value] of Object.entries(action.payload.activeFilters)) {
      if (Array.isArray(value) && value.length === 1 && key !== 'license_keys') {
        preferences[key] = value.toString();
      } else if (key === 'special_cases' && value === 'None of the above') {
        // Do nothing
      } else {
        preferences[key] = value;
      }
    }

    const queryString = getQueryString(preferences);
    const response = yield call(API_V1.getAllCliniciansAbie, { preferences: queryString });

    yield put(
      loadClinicians(
        response.data.clinicians.map((o: any) => {
          const isSupervised = o?.addresses[0]?.supervised_insurances.find(
            (f: any) => preferences.insurances === f.name,
          );
          if (isSupervised) {
            return {
              ...o,
              license_types: [...o.license_types, { ...supervisedCredential }],
            };
          }
          return {
            ...o,
            license_types: [...o.license_types, { ...licenseCredential }],
          };
        }),
      ),
    );
    yield call(handleGetFilteredClinicians);
    yield put(setLoadingClinicians(false));
  } catch (e: any) {
    yield put(setError(e.response?.status));
    yield put(setLoadingClinicians(false));
  }
}

function* handleGetDisplayedClinicians(action: any): any {
  const { page } = action.payload;
  const filteredClinicians = yield select(filteredCliniciansSelector);
  const paginatedResults = filteredClinicians.slice(0, page * 10);
  yield put(setPage(page));
  yield put(setDisplayedClinicians(paginatedResults));
  yield put(setClinicianCount(filteredClinicians.length));
  yield delay(100);
  yield put(setLoadingDisplayedClinicians(false));
}

function* matchesSaga(): any {
  yield all([
    yield takeLatest(t.GET_CLINICIANS, handleGetClinicians),
    yield takeLatest(t.GET_CLINICIANS_COUNT, handleGetCliniciansCount),
    yield takeLatest(t.GET_FILTERED_CLINICIANS, handleGetFilteredClinicians),
    yield takeLatest(t.GET_DISPLAYED_CLINICIANS, handleGetDisplayedClinicians),
  ]);
}

export default matchesSaga;
